import type { Cycle, Category, PersonInfo, Reviewer } from '../common/types';

export interface CollectiveAssessmentData {
    passed: number;
    failed: number;
    total: number;
    averageScore: number;
    passedAssessmentDistribution: Record<string, number>;
    failedAssessmentDistribution: Record<string, number>;
}

export interface FinalDecisionSubmission {
    id: string;
    submissionId: string;
    submission: {
        id: string;
        submissionNumber: string;
        title: string;
        abstract: string;
        manuscriptUrl: string;
        isbnNo: string;
        authors: string;
        submittedAt: string;
        status: number;
        applicationType: number;
        cycleId: number;
        categoryId: number;
        cycle: Cycle;
        category: Category;
        author: {
            userId: string;
            username: string;
            person: PersonInfo;
        };
        reviewers: Reviewer[];
    };
    chairman: {
        userId: string;
        person: PersonInfo;
    };
    finalScore: number;
    finalDecision: number;
    remarks: string;
    decidedAt: string;
    isNotified: boolean;
    notifiedAt: string;
}

export interface AssessmentDraftItem {
    submissionId: string;
    submissionNumber: string;
    title: string;
    geoPoliticalZone: string;
    status: string;
    submittedAt: string;
    fullName: string;
    categoryName: string;
    averageScore: number;
    categoryId: number;
    reviewers: {
        reviewerId: string;
        reviewerName: string;
        isCompleted: boolean;
        isActive: boolean;
        assignedAt: string;
        review: {
            score: number;
            remarks: string;
            submittedAt: string;
        } | null;
    }[];
}

export interface GetFinalDecisionPayload {
    categoryId: number;
    cycleId: number;
    applicationType: number;
    finalDecision: number;
}

// ── Report batch approval (enums mirror the backend) ────────────────────────────

export const PeriodType = {
    Custom: 1,
    Quarterly: 2,
} as const;
export type PeriodTypeValue = typeof PeriodType[keyof typeof PeriodType];

export const Quarter = {
    Q1: 1,
    Q2: 2,
    Q3: 3,
    Q4: 4,
} as const;
export type QuarterValue = typeof Quarter[keyof typeof Quarter];

export const BatchStatus = {
    Draft: 1,
    PendingApproval: 2,
    Approved: 3,
    ReadyForSubmission: 4,
    Rejected: 5,
} as const;
export type BatchStatusValue = typeof BatchStatus[keyof typeof BatchStatus];

export const ApprovalStatus = {
    Pending: 1,
    Approved: 2,
    Rejected: 3,
} as const;
export type ApprovalStatusValue = typeof ApprovalStatus[keyof typeof ApprovalStatus];

export const ApprovalType = {
    SuccessfulSubmissions: 1,
    FailedSubmissions: 2,
    ReviewerPayments: 3,
    PrimafacieAssessment: 4,
    SuccessfulReviewEvaluationAssessment: 5,
    ReturnedToReviewerAfterReviewerEvaluationAssessment: 6,
    ReceivedRevisedCopy: 7,
    JournalAcceptableForSupport: 8,
    NegativePrimafacieAssessment: 9,
    PositivePrimafacieAssessmentWithAssignedReviewer: 10,
    AuthorRevisedManuscriptWithUnsatisfactoryResponse: 11,
    JournalNotAcceptableForSupport: 12,
} as const;
export type ApprovalTypeValue = typeof ApprovalType[keyof typeof ApprovalType];

export const APPROVAL_TYPE_LABELS: Record<number, string> = {
    [ApprovalType.SuccessfulSubmissions]: 'Successful Submissions',
    [ApprovalType.FailedSubmissions]: 'Failed Submissions',
    [ApprovalType.ReviewerPayments]: 'Reviewer Payments',
    [ApprovalType.PrimafacieAssessment]: 'Prima Facie Assessment',
    [ApprovalType.SuccessfulReviewEvaluationAssessment]: 'Successful Review Evaluation Assessment',
    [ApprovalType.ReturnedToReviewerAfterReviewerEvaluationAssessment]: 'Returned to Reviewer After Reviewer Evaluation Assessment',
    [ApprovalType.ReceivedRevisedCopy]: 'Received Revised Copy',
    [ApprovalType.JournalAcceptableForSupport]: 'Journal Acceptable for Support',
    [ApprovalType.NegativePrimafacieAssessment]: 'Negative Prima Facie Assessment',
    [ApprovalType.PositivePrimafacieAssessmentWithAssignedReviewer]: 'Positive Prima Facie Assessment with Assigned Reviewer',
    [ApprovalType.AuthorRevisedManuscriptWithUnsatisfactoryResponse]: 'Author Revised Manuscript with Unsatisfactory Response',
    [ApprovalType.JournalNotAcceptableForSupport]: 'Journal Not Acceptable for Support',
};

export const BATCH_STATUS_LABELS: Record<number, string> = {
    [BatchStatus.Draft]: 'Draft',
    [BatchStatus.PendingApproval]: 'Pending Approval',
    [BatchStatus.Approved]: 'Approved',
    [BatchStatus.ReadyForSubmission]: 'Ready For Submission',
    [BatchStatus.Rejected]: 'Rejected',
};

// ── Report batch payloads / responses ───────────────────────────────────────────

export interface GenerateReportPayload {
    fromDate: string;
    toDate: string;
    approvalType: number;
}

export interface ApprovalReviewerDetail {
    id: string;
    name: string;
    phoneNumber: string;
    email: string;
    accountNumber: string;
}

export interface ApprovalAuthorDetail {
    id: string;
    name: string;
    isLeadAuthor: boolean;
    phoneNumber: string;
    email: string;
    institution: string;
}

export interface ApprovalRequestDetailItem {
    referenceId: string;
    title: string;
    remarks: string;
    submissionNumber: string;
    authorDetails: ApprovalAuthorDetail[] | null;
    reviewerDetails: ApprovalReviewerDetail[] | null;
}

export interface ApprovalReportData {
    totalRecords: number;
    approvalRequestDetail: ApprovalRequestDetailItem[] | null;
}

export interface CreateReportBatchPayload {
    referenceIds: string[];
    approvalType: number;
    fromDate: string;
    toDate: string;
    periodType: number;
    quarter?: number;
    isSubmitted: boolean;
}

export interface GetReportBatchPayload {
    pageNumber: number;
    pageSize: number;
    sortBy?: string;
    sortDescending?: boolean;
    search?: string;
    batchStatus?: number | null;
}

export interface UpdateReportBatchPayload {
    reportBatchId: string;
    addReferenceIds: string[];
    removeReferenceIds: string[];
    type: number;
    isSubmitted: boolean;
}

export interface ReportBatchItem {
    id: string;
    title: string;
    periodType: number;
    startDate: string;
    endDate: string;
    quarter: number;
    year: number;
    generatedById: string;
    generatedBy: string;
    reportType: number;
    totalRecords: number;
    status: number;
    periodLabel: string;
    active: boolean;
    createdDate: string;
    isDeleted: boolean;
}

export const APPROVAL_STATUS_LABELS: Record<number, string> = {
    [ApprovalStatus.Pending]: 'Pending',
    [ApprovalStatus.Approved]: 'Approved',
    [ApprovalStatus.Rejected]: 'Rejected',
};

export interface GetApprovalRequestPayload {
    pageNumber: number;
    pageSize: number;
    sortBy?: string;
    sortDescending?: boolean;
    search?: string;
    approvalStatus?: number | null;
}

export interface ApprovalRequestItem {
    id: string;
    reportBatchId: string;
    requestedById: string;
    nameOfRequestedUser: string;
    requestedAt: string;
    status: number;
    approvedById: string;
    nameOfApprovingUser: string;
    approvedAt: string;
    remark: string;
    title: string;
    totalRecords: number;
    periodLabel: string;
    hasNotifiedAuthor: boolean;
    reportType: number;
}

export interface ProcessApprovalPayload {
    approvalRequestId: string;
    isApproved: boolean;
    comment: string;
}
